import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import JobHeader from './components/JobHeader';
import JobDescription from './components/JobDescription';
import CompanyInfo from './components/CompanyInfo';
import ApplicationRequirements from './components/ApplicationRequirements';
import SimilarJobs from './components/SimilarJobs';
import jobService from '../../services/jobService';

const JobDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get jobId from location state
        const jobId = location?.state?.jobId;
        
        if (!jobId) {
          setError('Job not found. Please select a job from the listings.');
          setLoading(false);
          return;
        }

        // Fetch job details from database
        const job = await jobService?.getJobById(jobId);
        
        // Increment view count
        await jobService?.incrementViews(jobId);
        
        setJobData(job);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching job details:', err);
        setError(err?.message || 'Failed to load job details. Please try again.');
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [location?.state?.jobId]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 1024) {
        setShowMobileActions(window.scrollY > 300);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleApply = () => {
    if (jobData?.id) {
      navigate('/job-application', { state: { jobId: jobData?.id } });
    }
  };

  const handleSaveToWishlist = () => {
    setIsSaved(!isSaved);
    // TODO: Implement actual save/unsave functionality with Supabase
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading job details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !jobData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-error">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Unable to Load Job</h2>
                <p className="text-muted-foreground mb-6">{error || 'The job you are looking for could not be found.'}</p>
                <button
                  onClick={() => navigate('/job-seeker-dashboard')}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
                >
                  Back to Jobs
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Format job data for components
  const formattedJobData = {
    id: jobData?.id,
    title: jobData?.title,
    isFeatured: jobData?.urgentHiring || false,
    company: {
      name: jobData?.company,
      logo: jobData?.companyLogo,
      logoAlt: jobData?.companyLogoAlt,
      industry: "Information Technology & Services", // TODO: Add industry field to database
      employeeCount: "N/A", // TODO: Add to database
      headquarters: jobData?.location,
      overview: jobData?.requirements || "Company overview not available"
    },
    experience: jobData?.experience,
    location: jobData?.location,
    employmentType: jobData?.employmentTypeFormatted || jobData?.employmentType,
    salary: {
      min: jobData?.salaryMin,
      max: jobData?.salaryMax
    },
    openings: jobData?.openings,
    applicationDeadline: jobData?.deadline,
    workArrangement: jobData?.workModeFormatted || jobData?.workMode,
    description: jobData?.responsibilities || "Job description not available",
    responsibilities: jobData?.responsibilities?.split('\n')?.filter(Boolean) || [],
    qualifications: jobData?.qualifications?.split('\n')?.filter(Boolean) || jobData?.requirements?.split('\n')?.filter(Boolean) || [],
    preferredSkills: jobData?.tags || [],
    benefits: [], // TODO: Add benefits field to database
    reportingStructure: jobData?.department ? `This position reports to the ${jobData?.department} department.` : ""
  };

  const applicationRequirements = {
    documents: jobData?.documentRequirements || [],
    screeningProcess: [],
    additionalInfo: "Please submit your application through our portal. Selected candidates will be contacted within 2-3 weeks.",
    contactInfo: {
      email: "careers@company.com", // TODO: Add to database
      phone: "N/A" // TODO: Add to database
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${formattedJobData?.title} at ${formattedJobData?.company?.name} - JobPortal India`}</title>
        <meta name="description" content={`Apply for ${formattedJobData?.title} position at ${formattedJobData?.company?.name}. ${formattedJobData?.experience} experience required. Location: ${formattedJobData?.location}. Salary: ₹${(formattedJobData?.salary?.min / 100000)?.toFixed(1)}L - ₹${(formattedJobData?.salary?.max / 100000)?.toFixed(1)}L per annum.`} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <JobHeader
            job={formattedJobData}
            onApply={handleApply}
            onSaveToWishlist={handleSaveToWishlist}
            isSaved={isSaved}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-6">
                <JobDescription job={formattedJobData} />
                <ApplicationRequirements requirements={applicationRequirements} />
              </div>

              <div className="space-y-6">
                <CompanyInfo company={formattedJobData?.company} />
                {/* TODO: Implement similar jobs query */}
                {/* <SimilarJobs jobs={similarJobs} /> */}
              </div>
            </div>
          </div>
        </main>

        {showMobileActions && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border p-4 lg:hidden shadow-modal">
            <div className="flex gap-3">
              <button
                onClick={handleSaveToWishlist}
                className={`flex-shrink-0 w-12 h-12 rounded-lg border transition-smooth flex items-center justify-center ${
                  isSaved
                    ? 'border-error text-error bg-error/10' :'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={isSaved ? "currentColor" : "none"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
              </button>
              <button
                onClick={handleApply}
                className="flex-1 h-12 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
              >
                Apply Now
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default JobDetails;